import React, { useEffect, useMemo, useState } from 'react';
import {
  FiCalendar, FiCheck, FiCopy, FiExternalLink, FiSun, FiMoon,
  FiTarget, FiChevronDown, FiChevronRight, FiAlertTriangle, FiTrendingUp
} from 'react-icons/fi';

// =====================================================================
// Social Playbook — daily / weekly distribution checklist for the 30-day
// sprint. State persisted in localStorage (single-admin use case; can be
// promoted to a Supabase table later if multiple people need to track).
// =====================================================================

type Task = { id: string; label: string; note?: string };
type DayBlock = { day: number; date?: string; morning: Task[]; evening: Task[] };
type WeekBlock = { week: number; theme: string; days: DayBlock[]; milestone: string };
type Template = { id: string; title: string; body: string };

const STORAGE_KEY = 'snapchart_playbook_progress_v1';

// ── PRE-DEFINED CONTENT ──────────────────────────────────────────────────
const SETUP_TASKS: Task[] = [
  { id: 'setup-bio',       label: 'Optimise bio Twitter (cf. template)' },
  { id: 'setup-header',    label: 'Header image Twitter = share card NO-TRADE' },
  { id: 'setup-pinned',    label: 'Tweet épinglé = 7-Day Challenge' },
  { id: 'setup-sheet',     label: 'Crée Google Sheet de tracking quotidien' },
  { id: 'setup-list30',    label: 'Liste de 30 comptes traders FTMO à suivre' },
  { id: 'setup-stripe-coupon', label: 'Stripe coupon PRODUCTHUNT (Repeating 3 months, 50%)' },
  { id: 'setup-stripe-promo',  label: 'Stripe Promotion Code "PRODUCTHUNT"' },
  { id: 'setup-rotate-keys',   label: '🔴 Rotation clés sensibles (Stripe live, Resend, mdp admin)' },
  { id: 'setup-creatomate-fiverr', label: '(Optionnel) Commande template Creatomate Fiverr ~$100' },
];

const DAILY_TEMPLATE = (day: number, theme: string, extra: string): DayBlock => ({
  day,
  morning: [
    { id: `d${day}-m1`, label: 'Lance analyse Snap Chart (Gold ou BTC)' },
    { id: `d${day}-m2`, label: 'Clique Share → Post on X (caption auto)' },
    { id: `d${day}-m3`, label: 'Note dans Google Sheet (date, asset, verdict)' },
    { id: `d${day}-m4`, label: '5 replies VALEUR sur tweets traders FTMO' },
    { id: `d${day}-m5`, label: 'Like + RT 5 tweets de ta liste de 30' },
  ],
  evening: [
    { id: `d${day}-e1`, label: 'Check tweet matin (impressions, commentaires)' },
    { id: `d${day}-e2`, label: 'Répond aux commentaires reçus (< 30 min)' },
    { id: `d${day}-e3`, label: 'DM 2 traders FTMO ciblés' },
    { id: `d${day}-e4`, label: 'Note DMs envoyés + réponses dans Sheet' },
    ...(extra ? [{ id: `d${day}-extra`, label: `🎯 ${extra}` }] : []),
  ],
});

const PLAYBOOK: WeekBlock[] = [
  {
    week: 1,
    theme: 'Établir le format quotidien',
    milestone: '7 verdicts, 35 replies, 14 DMs, 1 post Reddit, +20-50 followers, 1-3 beta testers',
    days: [
      DAILY_TEMPLATE(1, 'L', 'RT ton tweet épinglé'),
      DAILY_TEMPLATE(2, 'M', 'Tweet screenshot du verdict d\'hier qui s\'est confirmé'),
      DAILY_TEMPLATE(3, 'M', 'Post Reddit r/Forex (cf. template)'),
      DAILY_TEMPLATE(4, 'J', 'Check les commentaires du Reddit post'),
      DAILY_TEMPLATE(5, 'V', 'Thread "5 things I learned letting AI veto my trades"'),
      DAILY_TEMPLATE(6, 'S', 'Weekly recap : verdicts cette semaine, capital saved'),
      DAILY_TEMPLATE(7, 'D', 'Bilan S1 : followers gagnés, top tweet, plan S2'),
    ],
  },
  {
    week: 2,
    theme: 'Build proof + recrute cohorte beta',
    milestone: '14 verdicts, 70 replies, 28 DMs, 3-7 beta testers, +50-150 followers',
    days: [
      DAILY_TEMPLATE(8, 'L', 'Screenshot Dashboard "Saved $X this week"'),
      DAILY_TEMPLATE(9, 'M', 'Tweet stats "Last week: 7 verdicts, 4 NO-TRADE, TDI 87/100"'),
      DAILY_TEMPLATE(10, 'M', 'Post 1 groupe Facebook FTMO (cf. template)'),
      DAILY_TEMPLATE(11, 'J', 'Onboarde 1ers beta testers (SQL activation)'),
      DAILY_TEMPLATE(12, 'V', 'Thread "What I learned in 11 days — 1/8"'),
      DAILY_TEMPLATE(13, 'S', 'Demande aux 2 testimonials existants vidéo 30s'),
      DAILY_TEMPLATE(14, 'D', 'Bilan S2 + plan S3'),
    ],
  },
  {
    week: 3,
    theme: 'Amplifier + Authority',
    milestone: '1 vidéo YouTube, newsletter lancée 10-30 subs, +100-250 followers, 5-10 beta testers',
    days: [
      DAILY_TEMPLATE(15, 'L', 'Screenshot Dashboard "Day 15: $X saved"'),
      DAILY_TEMPLATE(16, 'M', '🎥 Filme + edite vidéo YouTube (3h focus)'),
      DAILY_TEMPLATE(17, 'M', '🚀 Publie vidéo YouTube + tweet promo'),
      DAILY_TEMPLATE(18, 'J', 'DM 5 trader YouTubers (cf. template)'),
      DAILY_TEMPLATE(19, 'V', 'Thread "7 days of beta testers feedback"'),
      DAILY_TEMPLATE(20, 'S', 'Weekly recap + check YouTube views/comments'),
      DAILY_TEMPLATE(21, 'D', '📧 Lance newsletter Substack (1er email envoyé)'),
    ],
  },
  {
    week: 4,
    theme: 'Coup d\'éclat Product Hunt',
    milestone: 'PH launch, +200-500 followers total, 10-15 beta testers, 1-5 premiers clients payants',
    days: [
      DAILY_TEMPLATE(22, 'L', 'Prep PH page : 3 screenshots HD + GIF/vidéo 30s'),
      DAILY_TEMPLATE(23, 'M', 'Finalise PH description + tagline (260 chars)'),
      DAILY_TEMPLATE(24, 'M', 'Tweet teaser "Launching something Thursday"'),
      DAILY_TEMPLATE(25, 'J', 'DM 30 amis/early supporters pour upvote PH'),
      DAILY_TEMPLATE(26, 'V', 'Schedule tweets J-day toutes les 4h'),
      DAILY_TEMPLATE(27, 'S', 'Newsletter "Launching Monday, here\'s the story"'),
      DAILY_TEMPLATE(28, 'D', '🚀🚀🚀 LAUNCH DAY PRODUCT HUNT — workflow ci-dessous'),
    ],
  },
];

const TEMPLATES: Template[] = [
  {
    id: 'pinned-tweet',
    title: 'Tweet épinglé — 7-Day Challenge',
    body: `Launching the 7-Day Discipline Challenge.

Use snap-chart.ai for 7 days.
Follow every verdict: TRADE / NO-TRADE / WAIT.
Post your results with #SnapChartChallenge

Top discipline score = 3 months free ($447 value).

Who's in? 👇

#XAUUSD #Gold #Forex #FTMO #PropFirm`,
  },
  {
    id: 'dm-beta-recruit',
    title: 'DM recrutement beta tester (trader FTMO)',
    body: `Hey [name] 👋

Saw your tweet about [specific thing they tweeted].
Solid take.

I built an AI that gives a TRADE / NO-TRADE / WAIT verdict on
Gold + BTC charts. Specifically for FTMO/PropFirm rules
(structure, regime, risk).

Looking for 10 serious traders to test it for 14 days
in exchange for honest feedback. 3 months free Institutional
plan ($447 value) if interested.

XAUUSD/BTC traders only.

Let me know — happy to share the link.`,
  },
  {
    id: 'dm-followup',
    title: 'DM follow-up après "interested"',
    body: `Hey [name] 👋

Thanks for jumping on the beta. Quick steps to get you set up:

1. Sign up: snap-chart.ai/register
2. Confirm your email
3. Reply here with the email you signed up with

I'll activate your 3-month Institutional plan within an hour.

The deal:
✅ 3 months free, no card required
✅ Use it daily before your trades
✅ At day 14, post one honest review (tag me)

Any questions before signing up?`,
  },
  {
    id: 'reddit-post',
    title: 'Post Reddit r/Forex',
    body: `Title: I built an AI that tells me when NOT to trade Gold — here's what 17 days looks like

Body:
I'm a XAUUSD trader who kept getting blown up by FOMO trades. So I built an AI
that gives me a verdict on every chart: TRADE / NO-TRADE / WAIT — before I
risk a dollar.

After 17 days using it:
- 28 analyses run
- 17 NO-TRADE verdicts (60%)
- Capital saved (counterfactual): $X,XXX

The thing that surprised me most: how many "obvious" setups it killed.
Structure not confirmed, market regime wrong, etc.

If you trade Gold/BTC and want to test: snap-chart.ai/?ref=E866SU (free 30-day trial)

Happy to answer questions about the AI logic.`,
  },
  {
    id: 'facebook-post',
    title: 'Post Facebook groupe FTMO',
    body: `Looking for 10 serious traders to beta test an AI risk discipline tool.

What it does: Upload your chart → AI gives a TRADE / NO-TRADE / WAIT
verdict before you risk a dollar. Built specifically for FTMO /
PropFirm traders.

What you get: 3 months free Institutional plan ($447 value).

What I need from you: Use it daily for 2 weeks before your real trades,
and post ONE honest review at the end (good or bad — I need real feedback).

Preferred: XAUUSD or BTC traders. Min 6 months experience.

Drop "interested" below and I'll DM you the access. First 10 only.`,
  },
  {
    id: 'youtube-outreach',
    title: 'DM outreach trader YouTuber',
    body: `Hey [name], huge fan of your [specific video they made about prop firm].

I built a tool that does AI verdict (TRADE/NO-TRADE/WAIT) on Gold + BTC
charts — specifically for FTMO/PropFirm traders. Counterfactual capital
tracking so users see exactly what discipline saved them.

Would you be open to:
- A free 6-month account to try it
- If you like it, mention it in a future video (no commitment)
- 30% affiliate commission on signups via your link

10-min Loom demo: [enregistre une démo de 10 min]

No pressure either way. Let me know if interested.`,
  },
  {
    id: 'sql-beta-activate',
    title: 'SQL — Activer beta tester (3 mois Institutional)',
    body: `UPDATE public.user_profiles
SET
  subscription_plan_id = 'institutional_ai',
  subscription_status = 'active',
  billing_status = 'active',
  is_in_trial = false,
  paid_until = now() + interval '90 days',
  billing_type = 'comped',
  updated_at = now()
WHERE user_id = (
  SELECT id FROM auth.users WHERE email = 'their-email@example.com'
)
RETURNING user_id, subscription_plan_id, paid_until;`,
  },
  {
    id: 'ph-workflow',
    title: 'Product Hunt — Workflow J28',
    body: `00:01 EST — Submit ton produit MAINTENANT
00:05      — Tweet: "We're live on Product Hunt! [PH link]"
00:10      — DM tes 50 upvoters préparés en semaine 4
00:30      — Email à toute ta newsletter avec lien PH
07:00      — Verdict matin habituel + mention "also launching on PH today"
ALL DAY    — Réponds à CHAQUE commentaire sur PH dans les 15 min
            — Tweet toutes les 2-3h : "X hours in, currently #Y on PH!"
22:00      — Final push tweet: "Last hours to support 👇"
23:59      — Ranking se fige. Top 5 = victoire. Top 1-3 = jackpot.`,
  },
];

// ── COMPONENT ────────────────────────────────────────────────────────────
const SocialPlaybookPanel: React.FC = () => {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [openWeek, setOpenWeek] = useState<number | null>(1);
  const [openTemplate, setOpenTemplate] = useState<string | null>(null);
  const [copiedTemplate, setCopiedTemplate] = useState<string | null>(null);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setDone(JSON.parse(raw));
    } catch { /* noop */ }
  }, []);

  const toggle = (id: string) => {
    setDone((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const copyTemplate = (t: Template) => {
    try {
      navigator.clipboard.writeText(t.body);
      setCopiedTemplate(t.id);
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch { /* noop */ }
  };

  // Progress calc
  const allTasks = useMemo(() => {
    const ids: string[] = [
      ...SETUP_TASKS.map(t => t.id),
      ...PLAYBOOK.flatMap(w => w.days.flatMap(d => [...d.morning.map(t => t.id), ...d.evening.map(t => t.id)])),
    ];
    return ids;
  }, []);
  const completed = allTasks.filter(id => done[id]).length;
  const totalPct = Math.round((completed / allTasks.length) * 100);

  // Today's day (1-30 indexed from sprint start, fallback day 1)
  const sprintStartIso = (() => {
    try { return localStorage.getItem('snapchart_playbook_start') || ''; } catch { return ''; }
  })();
  const sprintDayToday = useMemo(() => {
    if (!sprintStartIso) return 1;
    const start = new Date(sprintStartIso).getTime();
    const today = Date.now();
    const dayN = Math.floor((today - start) / 86400000) + 1;
    return Math.max(1, Math.min(30, dayN));
  }, [sprintStartIso]);

  const setSprintStart = (iso: string) => {
    try { localStorage.setItem('snapchart_playbook_start', iso); window.location.reload(); } catch { /* noop */ }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FiTrendingUp className="text-cyan-400" /> Social Distribution Playbook
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Plan 30 jours pour devenir « le mec du verdict matinal ». Coche ce que tu fais. État sauvegardé local.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {sprintStartIso ? (
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wider">Sprint day</div>
              <div className="text-2xl font-bold text-cyan-400 tabular-nums">{sprintDayToday}/30</div>
            </div>
          ) : (
            <button
              onClick={() => setSprintStart(new Date().toISOString().slice(0,10))}
              className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 rounded-lg text-sm font-medium"
            >
              🚀 Démarrer le sprint aujourd'hui
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-400">Progression globale</span>
          <span className="text-white font-semibold">{completed} / {allTasks.length} tâches ({totalPct}%)</span>
        </div>
        <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all"
            style={{ width: `${totalPct}%` }}
          />
        </div>
      </div>

      {/* SETUP — toujours visible */}
      <Section title="⚙️ Setup initial (à faire AVANT jour 1)" icon={FiAlertTriangle} accent="amber">
        <div className="space-y-1.5">
          {SETUP_TASKS.map((t) => (
            <TaskRow key={t.id} task={t} checked={!!done[t.id]} onToggle={() => toggle(t.id)} />
          ))}
        </div>
      </Section>

      {/* WEEKS */}
      {PLAYBOOK.map((w) => {
        const weekTasks = w.days.flatMap(d => [...d.morning.map(t => t.id), ...d.evening.map(t => t.id)]);
        const weekDone = weekTasks.filter(id => done[id]).length;
        const weekPct = Math.round((weekDone / weekTasks.length) * 100);
        const isOpen = openWeek === w.week;

        return (
          <div key={w.week} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => setOpenWeek(isOpen ? null : w.week)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/[0.03] transition"
            >
              <div className="flex items-center gap-3 text-left">
                <FiCalendar className="text-cyan-400" />
                <div>
                  <div className="text-white font-semibold">Semaine {w.week} — {w.theme}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{w.milestone}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-400 tabular-nums">{weekDone}/{weekTasks.length} · {weekPct}%</div>
                {isOpen ? <FiChevronDown /> : <FiChevronRight />}
              </div>
            </button>
            {isOpen && (
              <div className="p-4 pt-0 space-y-3">
                {w.days.map((d) => {
                  const isToday = sprintDayToday === d.day && !!sprintStartIso;
                  return (
                    <div key={d.day} className={`rounded-lg border p-3 ${isToday ? 'border-cyan-500/40 bg-cyan-500/5' : 'border-white/10 bg-white/[0.02]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-white">
                          Jour {d.day} {isToday && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300">Today</span>}
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-amber-400 mb-1.5 flex items-center gap-1"><FiSun className="w-3 h-3"/> Bloc matin (30 min)</div>
                          <div className="space-y-1">
                            {d.morning.map((t) => <TaskRow key={t.id} task={t} checked={!!done[t.id]} onToggle={() => toggle(t.id)} compact />)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-purple-400 mb-1.5 flex items-center gap-1"><FiMoon className="w-3 h-3"/> Bloc soir (30 min)</div>
                          <div className="space-y-1">
                            {d.evening.map((t) => <TaskRow key={t.id} task={t} checked={!!done[t.id]} onToggle={() => toggle(t.id)} compact />)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* TEMPLATES — copy/paste */}
      <Section title="📝 Templates copier-coller" icon={FiCopy} accent="cyan">
        <div className="space-y-2">
          {TEMPLATES.map((t) => {
            const isOpen = openTemplate === t.id;
            const isCopied = copiedTemplate === t.id;
            return (
              <div key={t.id} className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenTemplate(isOpen ? null : t.id)}
                  className="w-full p-3 flex items-center justify-between hover:bg-white/[0.03] text-left"
                >
                  <span className="text-sm text-white">{t.title}</span>
                  {isOpen ? <FiChevronDown className="text-gray-400" /> : <FiChevronRight className="text-gray-400" />}
                </button>
                {isOpen && (
                  <div className="p-3 pt-0">
                    <pre className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-gray-300 whitespace-pre-wrap font-mono max-h-80 overflow-y-auto">{t.body}</pre>
                    <button
                      onClick={() => copyTemplate(t)}
                      className="mt-2 px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 text-cyan-300 rounded-md text-xs font-medium flex items-center gap-1.5"
                    >
                      <FiCopy className="w-3 h-3" /> {isCopied ? 'Copié ✓' : 'Copier le template'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Quick links */}
      <Section title="🔗 Liens utiles" icon={FiExternalLink} accent="emerald">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {[
            { label: 'Twitter / X', href: 'https://x.com' },
            { label: 'Reddit r/Forex', href: 'https://reddit.com/r/Forex' },
            { label: 'Reddit r/Daytrading', href: 'https://reddit.com/r/Daytrading' },
            { label: 'Product Hunt Submit', href: 'https://www.producthunt.com/posts/new' },
            { label: 'Substack (newsletter)', href: 'https://substack.com' },
            { label: 'Stripe Coupons', href: 'https://dashboard.stripe.com/coupons' },
            { label: 'Stripe Promotion Codes', href: 'https://dashboard.stripe.com/promotion_codes' },
            { label: 'Supabase SQL Editor', href: 'https://supabase.com/dashboard/project/pkelsbstyskaktncgncs/sql/new' },
            { label: 'Hypefury (scheduler)', href: 'https://hypefury.com' },
          ].map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
              className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-gray-300 hover:text-cyan-400 flex items-center justify-between">
              {l.label} <FiExternalLink className="w-3 h-3" />
            </a>
          ))}
        </div>
      </Section>

      {/* Reset button */}
      <div className="pt-4 border-t border-white/5 flex justify-between items-center">
        <p className="text-xs text-gray-500">
          État sauvegardé dans ton navigateur (localStorage). Si tu change de PC, l'état ne suit pas.
        </p>
        <button
          onClick={() => {
            if (confirm('Reset toute la progression du playbook ?')) {
              try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem('snapchart_playbook_start'); } catch { /* noop */ }
              setDone({});
              window.location.reload();
            }
          }}
          className="text-xs text-gray-500 hover:text-red-400"
        >
          Reset progression
        </button>
      </div>
    </div>
  );
};

// ── HELPERS ──────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, accent, children }: { title: string; icon: any; accent: 'cyan' | 'amber' | 'emerald'; children: React.ReactNode }) {
  const colors = {
    cyan: 'border-cyan-500/20 bg-cyan-900/5',
    amber: 'border-amber-500/20 bg-amber-900/5',
    emerald: 'border-emerald-500/20 bg-emerald-900/5',
  };
  return (
    <div className={`border rounded-xl p-4 ${colors[accent]}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`text-${accent}-400`} />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function TaskRow({ task, checked, onToggle, compact }: { task: Task; checked: boolean; onToggle: () => void; compact?: boolean }) {
  return (
    <label className={`flex items-start gap-2 ${compact ? 'py-0.5' : 'py-1'} cursor-pointer group`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-cyan-500 focus:ring-cyan-500/50 cursor-pointer flex-shrink-0"
      />
      <span className={`text-sm ${checked ? 'text-gray-500 line-through' : 'text-gray-300 group-hover:text-white'}`}>
        {task.label}
      </span>
    </label>
  );
}

export default SocialPlaybookPanel;
